import React, { ChangeEvent, InvalidEvent, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormLabel,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisitPopup from './VisitPopup';
import SearchIcon from '@mui/icons-material/Search';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import './VisitRequest.css';
import {
  getVisitRequestBasicAPI,
  getVisitRequestIdAPI,
  getVisitRequestManagersAPI,
  postVisitRequestAPI,
  putVisitRequestApproveIdAPI,
  putVisitRequestCancelIdAPI,
  putVisitRequestIdAPI,
  putVisitRequestRejectedIdAPI
} from 'apis/visitRequest/visitRequestAPI';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisitRequestButton from './VisitRequestStatusBtn';
import VisitRequestBtn from './VisitRequestBtn';
import useUserStore from 'store/useUserStore';
import { useNavigate, useParams } from 'react-router-dom';
import SecurityAgreementModal from './SecurityAgreementModal';
import { USER_TYPE } from '../../constants/constants';
import SnackbarAlert from 'components/SnackbarAlert';
import { useSnackbarStore } from 'store/useSnackbarStore';
import { useTranslation } from 'react-i18next';
import { PersonAddAlt1 } from '@mui/icons-material';
import { useVisitRequestStore } from '../../store/useVisitRequestStore';

// 신청자 데이터 인터페이스
export interface TRequester {
  id: number;
  type: string;
  name: string;
  birth: string;
  phoneNumber: string;
  email: string;
  companyName: string;
  companyPhoneNumber: string;
}
interface TBusinessPlaces {
  id: number;
  name: string;
}
export interface TDigitalDevices {
  id: number;
  name: string;
}

// 재신청용 내방신청 정보
export interface TVisitRequest {
  businessPlaceId: number;
  managerId: number;
  managerEmail: string;
  visitors: TVisitor[];
}

// 방문자 정보 데이터 인터페이스
export interface TVisitor {
  id: number;
  companyName: string;
  companyPhoneNumber: string;
  name: string;
  phoneNumber: string;
  email: string;
  birth: string;
  bloodType: string;
  carNumber: string;
  isVip?: boolean;
  note: string;
  digitalDevices?: TVisitorDigitalDevices[];
  [key: string]: any;
}
export interface TVisitorDigitalDevices {
  id?: number;
  digitalDeviceId: number;
  modelName: string;
  serialNumber: string;
  note: string;
}

export const initVisitorData = {
  id: 0,
  companyName: '',
  companyPhoneNumber: '',
  name: '',
  phoneNumber: '',
  email: '',
  birth: '',
  bloodType: '',
  carNumber: '',
  isVip: false,
  note: '',
  digitalDevices: [
    {
      id: 0,
      digitalDeviceId: 0,
      modelName: '',
      serialNumber: '',
      note: ''
    }
  ]
};

//** 내방신청 페이지 **//
export default function VisitRequest() {
  const navigate = useNavigate();
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const { user } = useUserStore(); // 로그인 한 사람의 정보 (isAdmin에 사용됨)
  const { id } = useParams(); // URL에서 ID 값 가져오기
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const [openPopup, setOpenPopup] = useState(false);
  const [isAgreed, setIsAgreed] = useState<boolean>(false); // 약관 동의 상태 관리
  const [securityPolicyId, setSecurityPolicyId] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // 기준정보
  const [hasBasic, setHasBasic] = useState(false);
  const [basicRequester, setBasicRequester] = useState<TRequester>();
  const [basicBusinessPlaces, setBasicBusinessPlaces] = useState<TBusinessPlaces[]>([]);
  const [basicDigitalDevices, setBasicDigitalDevices] = useState<TDigitalDevices[]>([]);

  // 신청정보
  const [requester, setRequester] = useState<TRequester>(); // 신청자
  const [status, setStatus] = useState<string>(''); // 상태
  const [rejectedReason, setRejectedReason] = useState<string>(''); // 반려사유
  const [businessPlaceId, setBusinessPlaceId] = useState(0); // 사업장 ID
  const [managerTouched, setManagerTouched] = useState(false); // 담당자 필드가 포커스를 벗어났는지 여부
  const [managerEmail, setManagerEmail] = useState<string>(''); // 담당자 Email
  const [managerId, setManagerId] = useState(0); // 조회에 성공한 담당자 Email ID
  const [managerErrMsg, setManagerErrMsg] = useState<string>(''); // 매니저 API에러 여부
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [times, setTimes] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [purpose, setPurpose] = useState<string>(''); // 목적
  const [maxVisitorId, setMaxVisitorId] = useState(0);
  const [visitors, setVisitors] = useState<TVisitor[]>([]);
  const addVisitor = (visitor: TVisitor) => {
    setVisitors((prevVisitors) => [...prevVisitors, visitor]);
  };
  const updateVisitor = (updatedVisitor: TVisitor) => {
    setVisitors((visitors) => visitors.map((visitor) => (visitor.id === updatedVisitor.id ? { ...visitor, ...updatedVisitor } : visitor)));
  };
  const removeVisitor = (visitorId: number) => {
    setVisitors((prevVisitors) => prevVisitors.filter((visitor) => visitor.id !== visitorId));
  };

  const [selectedVisitor, setSelectedVisitor] = useState<TVisitor>(initVisitorData);

  const [isEditDisableStatus, setIsEditDisableStatus] = useState(false); // 취소, 반려의 경우 편집 불가능
  const [isRequester, setIsRequester] = useState(false); // 신청한 사람인지
  const [isManager, setIsManager] = useState(false); // 로그인한 사람이랑 담당자랑 같은 사람인지(승인, 반려 버튼보여지고 수정,삭제 버튼안나오고 신청정보 수정 불가능하게 하는 상태관리)

  // 재신청
  const { hasVisitRequest, visitRequest, setVisitRequest, initVisitRequest } = useVisitRequestStore();

  const fetchBasicData = async () => {
    try {
      const { data } = await getVisitRequestBasicAPI();
      setBasicRequester(data.requester); // 로그인한 사용자의 정보
      setBasicBusinessPlaces(data.businessPlaces); // 사업장 종류 불러와서 셋업
      setBasicDigitalDevices(data.digitalDevices); // 디지털기기 종류 불러와서 셋업
      setHasBasic(true);
    } catch (error) {
      // console.log('error', error);
      console.error(error);
    }
  };
  const fetchItemBasicData = async () => {
    try {
      const { data } = await getVisitRequestBasicAPI();
      setBasicBusinessPlaces(data.businessPlaces); // 사업장 종류 불러와서 셋업
      setBasicDigitalDevices(data.digitalDevices); // 디지털기기 종류 불러와서 셋업
    } catch (error) {
      // console.log('error', error);
    }
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    // 기본데이터는 항상 가져옴
    (async () => {
      setIsLoading(true);
      setHasBasic(false);
      await fetchBasicData();
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      await fetchItemBasicData();
    })();
  }, [language]);

  const fetchVisitRequest = async () => {
    try {
      const { code, data } = await getVisitRequestIdAPI(Number(id));
      if (code != 200) {
        navigate('/visit/request');
      }

      setRequester(data.requester); // 신청자
      setStatus(data.status); // 상태
      setRejectedReason(data.request.rejectedReason); // 반려사유
      setBusinessPlaceId(data.request.businessPlaceId); // 사업장 ID
      setManagerTouched(false); // 담당자 필드가 포커스를 벗어났는지 여부
      setManagerEmail(data.request.managerEmail); // 담당자 Email
      setManagerId(data.request.managerId); // 조회에 성공한 담당자 Email ID
      setManagerErrMsg(''); // 매니저 API에러 여부
      setDates([dayjs(data.request.visitFrom), dayjs(data.request.visitTo)]);
      if (data.request.visitStartTime && data.request.visitEndTime) {
        setTimes([dayjs(`${data.request.visitFrom} ${data.request.visitStartTime}`), dayjs(`${data.request.visitTo} ${data.request.visitEndTime}`)]);
      }
      setPurpose(data.request.purpose); // 목적
      // 신청자와 로그인한 사람이 다르면 변경 불가
      if (data.requester.id !== user?.id) {
        setIsEditDisableStatus(true);
      } // 반려, 취소, 승인났을때, 변경 불가능하게 상태 설정
      else if (['REJECTED', 'CANCELED', 'APPROVED'].includes(data.status) && Boolean(id)) {
        setIsEditDisableStatus(true);
      } else {
        setIsEditDisableStatus(false);
      }

      // 신청한 사람이 맞는지
      if (data.requester.id === user?.id) {
        setIsRequester(true);
      } else {
        setIsRequester(false);
      }
      // 담당자와 로그인한 사람이 같다면, isManager은 true
      if (data.request.managerId === user?.id) {
        setIsManager(true);
      } else {
        setIsManager(false);
      }

      setVisitors(data.visitors);

      setMaxVisitorId(Math.max(...data.visitors.map((visitor: { id: number }) => visitor.id)) || 0);
    } catch (error) {
      console.error(error);
    }
  };

  // 가상의 아이디
  const getNewVisitorId = () => {
    const newId = maxVisitorId + 1;
    setMaxVisitorId(newId);
    return newId;
  };

  // 기본정보가 있을 경우 실행
  useEffect(() => {
    if (!hasBasic) return;

    // id가 있으면 기존 내방신청 조회 (오류 시 신규 생성)
    if (id) {
      (async () => {
        await fetchVisitRequest();

        setIsLoading(false);
      })();
    }
    // id가 없으면 신규 내방신청 (데이터 초기화)
    else {
      if (!basicRequester) {
        return;
      }
      setRequester(basicRequester);
      setStatus(''); // 상태
      setRejectedReason(''); // 반려사유
      setBusinessPlaceId(hasVisitRequest ? visitRequest?.businessPlaceId : 0); // 사업장 ID
      setManagerId(hasVisitRequest ? visitRequest.managerId : 0); // 조회에 성공한 담당자 Email ID
      setManagerEmail(hasVisitRequest ? visitRequest?.managerEmail : ''); // 담당자 Email
      setManagerTouched(false); // 담당자 필드가 포커스를 벗어났는지 여부
      setManagerErrMsg(''); // 매니저 API에러 여부
      setDates([null, null]);
      setTimes([null, null]);
      setPurpose(''); // 목적
      setIsEditDisableStatus(false);
      setIsManager(false);

      // 재신청 데이터가 있으면
      if (hasVisitRequest) {
        setVisitors(visitRequest?.visitors || []);

        // 재신청 데이터 초기화
        initVisitRequest();
      }
      // // 신청자가 임직원이 아니고 방문자에 등록돼있지 않다면 신청자를 방문자에 추가
      else if (basicRequester?.type && basicRequester?.type != USER_TYPE.EMPLOYEE.key) {
        setVisitors([
          {
            id: 1,
            companyName: basicRequester?.companyName || '',
            companyPhoneNumber: basicRequester?.companyPhoneNumber || '',
            name: basicRequester?.name || '',
            phoneNumber: basicRequester?.phoneNumber || '',
            email: basicRequester?.email || '',
            birth: basicRequester?.birth || '',
            bloodType: '',
            carNumber: '',
            note: '',
            digitalDevices: [
              {
                id: 0,
                digitalDeviceId: 0,
                modelName: '',
                serialNumber: '',
                note: ''
              }
            ]
          }
        ]);
      } else {
        setVisitors([]);
      }

      setIsLoading(false);
    }
  }, [hasBasic]);
  const theme = useTheme();

  // 방문자 수정 버튼 클릭 시 팝업 열기
  const handleEditClick = (visitor: TVisitor) => {
    setSelectedVisitor(visitor);
    setOpenPopup(true);
  };

  // 방문자 추가 버튼 클릭 시 팝업 열기
  const handleAddClick = () => {
    setSelectedVisitor(initVisitorData); // visitor 정보를 초기화
    setOpenPopup(true); // 팝업 열기
  };

  // 팝업 닫기
  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedVisitor(initVisitorData);
  };

  // 방문일자 변경 핸들러
  const handleDateChange = (newDate: Dayjs | null, type: 'start' | 'end') => {
    if (type === 'start') {
      const endDate = dates[1];
      if (endDate && newDate && newDate.isAfter(endDate)) {
        // 시작 날짜가 끝 날짜보다 뒤인 경우 두 날짜를 교환
        setDates([endDate, newDate]);
      } else {
        setDates([newDate, endDate]);
      }
    } else {
      const startDate = dates[0];
      if (startDate && newDate && newDate.isBefore(startDate)) {
        // 끝 날짜가 시작 날짜보다 앞인 경우 두 날짜를 교환
        setDates([newDate, startDate]);
      } else {
        setDates([startDate, newDate]);
      }
    }
  };
  // 내방시간 변경 핸들러
  const handleTimeChange = (newTime: Dayjs | null, type: 'start' | 'end') => {
    if (type === 'start') {
      const endTime = times[1] ? times[1] : newTime?.add(1, 'hour') || newTime;
      setTimes([newTime, endTime]);
    } else {
      const startTime = times[0] ? times[0] : newTime?.subtract(1, 'hour') || newTime;
      setTimes([startTime, newTime]);
    }
  };
  // 사업장 변경핸들러임
  const handleBusinessPlaceIdChange = (event: SelectChangeEvent) => {
    const businessPlaceId = Number(event.target.value) || 0;
    setBusinessPlaceId(businessPlaceId);
  };
  // 담당자 필드에서 포커스를 벗어날 때 호출
  const handleManagerBlur = async () => {
    setManagerErrMsg(''); // 호출 전 에러메세지 초기화
    if (managerEmail) {
      setManagerTouched(true); // 사용자가 필드를 벗어났음을 표시
      //API호출
      try {
        const response = await getVisitRequestManagersAPI(managerEmail);
        // console.log('getVisitRequestManagersAPI API data : ', response.data.id);
        const id = response.data.id;
        setManagerId(id);
      } catch (error: any) {
        // 서버에서 에러났을때 메세지
        const errorMessage = error.data.message || '알 수 없는 오류가 발생했습니다.';

        setManagerErrMsg(errorMessage); // 에러 메시지 상태 업데이트
        setManagerId(0);
      }
    }
  };
  // 담당자 메일주소 변경
  const handleManagerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManagerEmail(e.target.value);
    setManagerTouched(false); // 필드가 변경되면 초기화
    setManagerId(0); // 성공 메시지가 사라지도록 초기화
    setManagerErrMsg(''); // 에러 메시지 초기화
  };

  // 목적 변경 감지
  const handlePurposeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= 200) {
      setPurpose(newValue);
    }
  };
  // 방문자 정보 입력이 완전히 되었는지 확인
  const checkVisitorDataComplete = (visitors: TVisitor[]) => {
    const isComplete = visitors.every((visitor) => {
      const { companyName, companyPhoneNumber, name, phoneNumber, email, birth, bloodType, digitalDevices } = visitor;
      // 기본 필수 정보 체크
      const basicInfoComplete = companyName && companyPhoneNumber && name && phoneNumber && email && birth && bloodType;
      // digitalDevices 필수 정보 체크
      const digitalDevicesComplete = digitalDevices ? digitalDevices.every((device) => device.digitalDeviceId && device.modelName) : true;
      // 모든 필수 정보가 완전한지 체크
      return basicInfoComplete && digitalDevicesComplete;
    });

    if (!isComplete) {
      showSnackbar('방문자 정보를 모두 입력해주세요.', 'warning');
      return false;
    }

    return true;
  };

  // 신청 버튼 클릭시 로직
  const handleSubmit = async () => {
    // 유효성 검사
    if (!businessPlaceId || businessPlaceId == 0) return showSnackbar(t('visit_request.error_messages.business_place_required'), 'warning');
    if (!dates[0] || !dates[1]) return showSnackbar(t('visit_request.error_messages.visit_date_required'), 'warning');
    if (!managerId || managerId == 0) return showSnackbar(t('visit_request.error_messages.manager_required'), 'warning');
    if (!purpose) return showSnackbar(t('visit_request.error_messages.purpose_required'), 'warning');
    if (visitors.length === 0) return showSnackbar(t('visit_request.error_messages.visitors_required'), 'warning');
    const isDataValid = checkVisitorDataComplete(visitors);
    if (!isDataValid) return;

    // 모든 입력이 유효할 경우 API 호출
    try {
      const requestData = {
        businessPlaceId,
        managerId: managerId, //  managerId
        visitFrom: dates[0].format('YYYY-MM-DD'), // 시작 날짜를 'YYYY-MM-DD' 형식으로 변환
        visitTo: dates[1].format('YYYY-MM-DD'), // 종료 날짜를 'YYYY-MM-DD' 형식으로 변환
        visitStartTime: times[0]?.isValid() ? times[0].format('HH:mm:ss') : '',
        visitEndTime: times[1]?.isValid() ? times[1].format('HH:mm:ss') : '',
        purpose,
        securityPolicyId: securityPolicyId,
        agreeToSecurity: isAgreed
      };

      // id를 제외한 새로운 배열 생성
      const visitorsWithoutId = visitors.map(({ id, ...rest }) => rest);

      const data = {
        requester: basicRequester,
        request: requestData,
        visitors: visitorsWithoutId
      };

      const response = await postVisitRequestAPI(data);

      // response가 성공적으로 반환된 경우에만 showSnackbar와 navigate 실행
      if (response?.data?.data?.id) {
        showSnackbar(t('visit_request.error_messages.request_success'), 'success');
        navigate(`/visit/request/${response.data.data.id}`);
      } else {
        // response가 예상과 다를 경우의 예외 처리
        showSnackbar(t('visit_request.error_messages.request_warn'), 'error');
      }
    } catch (error: any) {
      console.error('방문 신청 중 오류 발생:', error);
      // 에러 메세지 서버에 온거 그대로 보여주면 됨 TODO: 어떻게 형식오는지오고 바꿔야함
      showSnackbar(error?.data?.message, 'error');
    }
  };

  const handleResubmit = () => {
    const visitRequest: TVisitRequest = {
      businessPlaceId: businessPlaceId,
      managerId: managerId,
      managerEmail: managerEmail,
      visitors: visitors
    };

    setVisitRequest(visitRequest);

    navigate('/visit/request');
  };

  // 방문자 정보 삭제버튼
  const handleDeleteClick = (id: number) => {
    removeVisitor(id); // 클릭 시 해당 visitor를 삭제
  };

  const handleChange = async () => {
    // 유효성 검사
    if (!businessPlaceId || businessPlaceId == 0) return showSnackbar(t('visit_request.error_messages.business_place_required'), 'warning');
    if (!dates[0] || !dates[1]) return showSnackbar(t('visit_request.error_messages.visit_date_required'), 'warning');
    if (!managerId || managerId == 0) return showSnackbar(t('visit_request.error_messages.manager_required'), 'warning');
    if (!purpose) return showSnackbar(t('visit_request.error_messages.purpose_required'), 'warning');
    if (visitors.length === 0) return showSnackbar(t('visit_request.error_messages.visitors_required'), 'warning');

    const isDataValid = checkVisitorDataComplete(visitors);
    if (!isDataValid) return;

    // 변경 처리 로직
    const requestData = {
      businessPlaceId,
      managerId: managerId, //  managerId
      visitFrom: dates[0]?.format('YYYY-MM-DD'), // 시작 날짜를 'YYYY-MM-DD' 형식으로 변환
      visitTo: dates[1]?.format('YYYY-MM-DD'), // 종료 날짜를 'YYYY-MM-DD' 형식으로 변환
      visitStartTime: times[0]?.isValid() ? times[0].format('HH:mm:ss') : '',
      visitEndTime: times[1]?.isValid() ? times[1].format('HH:mm:ss') : '',
      purpose
    };

    const data = {
      request: requestData,
      visitors: visitors
    };

    if (id) {
      try {
        const response = await putVisitRequestIdAPI(Number(id), data);
        if (response.data.code === 200) {
          navigate(`/visit/request/${id}`);
          showSnackbar(t('visit_request.error_messages.request_change_success'), 'success'); // 변경 되었음을 표시하는 모달
        } else {
          showSnackbar(t('visit_request.error_messages.request_change_warn'), 'warning');
        }
      } catch (error: any) {
        showSnackbar(error.data.message, 'error');
      }
    }
  };
  // 취소 처리 로직
  const handleCancel = async () => {
    if (id) {
      try {
        const response = await putVisitRequestCancelIdAPI(Number(id), '');
        if (response.data.code === 200) {
          window.location.reload();
          showSnackbar(t('visit_request.error_messages.request_cancel_success'), 'success'); // 신청취소 되었음을 표시하는 모달
        } else {
          showSnackbar(t('visit_request.error_messages.request_cancel_warn'), 'warning');
        }
      } catch (error) {
        showSnackbar(t('visit_request.error_messages.error'), 'error'); // 신청취소가 에러났음을 표시하는 모달
      }
    }
  };
  // 승인 처리 로직
  const handleApprove = async () => {
    if (id) {
      const response = await putVisitRequestApproveIdAPI(Number(id), '');
      if (response.data.code == 200) {
        window.location.reload();
      }
    }
  };
  // 반려 처리 로직
  const handleReject = async (reason: string) => {
    const data = {
      rejectedReason: reason
    };
    // API 호출 전에 상태를 명확히 확인
    if (id && reason.trim()) {
      const response = await putVisitRequestRejectedIdAPI(Number(id), data);
      if (response.data.code == 200) {
        window.location.reload();
      }
    } else {
      alert('반려 사유를 입력해주세요.');
      return;
    }
  };

  // 약관 동의 핸들러
  const handleAgree = (securityPolicyId: number) => {
    setIsAgreed(true);
    setSecurityPolicyId(securityPolicyId);
  };

  // 커스텀 에러 메시지 설정 함수
  const handleInvalid = (e: InvalidEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(t('validation.required'));
  };

  // 입력이 있을 경우 에러 메시지 제거 함수
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity('');
  };
  return (
    <Box>
      {/* 신청 누른 후 팝업 */}
      <SnackbarAlert />

      {/* 보안서약 약관 동의 모달 */}
      {!isAgreed && !id && <SecurityAgreementModal isAgreed={isAgreed} handleAgree={handleAgree} />}

      {/* 동의한 경우에만 컴포넌트 내용 렌더링 */}
      {!isLoading && ((isAgreed && !id) || id) ? (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localStorage.getItem('language') || 'ko'}>
          <Box
            minWidth={320}
            padding={2}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
              // 모바일 뷰에서 패딩 조정
              padding: { xs: 2, sm: 2 },
              width: { xs: '100vw', sm: '100%' } //너비 조정
            }}
          >
            {/* 신청자 정보 */}
            <Box
              borderRadius={1}
              bgcolor={theme.palette.contentBlock.background}
              padding={2}
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{
                // 모바일 뷰에서 내부 간격 조정
                padding: { xs: 2, sm: 2 },
                gap: { xs: 1, sm: 2 }
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                {t('visit_request.applicant_information')}
              </Typography>
              <Box borderRadius={1} bgcolor={theme.palette.contentBlock.main} boxShadow={1} padding={2} display="flex" flexDirection="column" gap={2}>
                <Typography variant="body1" fontWeight="bold">
                  {requester?.name}
                </Typography>

                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 4 }}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.applicant_information_list.birth')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {requester?.birth}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.applicant_information_list.phone_number')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {requester?.phoneNumber}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography width={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.applicant_information_list.company_name')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {requester?.companyName}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.applicant_information_list.company_phone_number')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {requester?.companyPhoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* 방문신청 정보 */}
            <Box borderRadius={1} bgcolor={theme.palette.contentBlock.background} padding={2} display="flex" flexDirection="column" gap={2}>
              <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Typography variant="h5" fontWeight="bold">
                  {t('visit_request.visit_request_information')}
                </Typography>
                {/* 방문 요청 상태에 따른 버튼 표시  - 공통컴포넌트로 뺌 */}
                {Boolean(id) && status && (
                  <VisitRequestButton
                    status={status != 'APPROVED' || visitors?.some((visitor) => !visitor.securityManageNumber) ? status : 'REQUEST_COMPLETED'}
                  />
                )}
              </Box>
              {/* 방문 요청 상태가 반려의 경우, 반려사유 */}
              {Boolean(id) && status === 'REJECTED' && rejectedReason && (
                <Box borderRadius={1} bgcolor={theme.palette.contentBlock.main} boxShadow={1} padding={2} display="flex" flexDirection="row" gap={1}>
                  <Typography minWidth={100} variant="caption" color="#D32F2F" fontWeight="bold">
                    {t('visit_request.reject_reason')}
                  </Typography>
                  <Box>{rejectedReason}</Box>
                </Box>
              )}

              <Box borderRadius={1} bgcolor={theme.palette.contentBlock.main} boxShadow={1} padding={2} display="flex" flexDirection="column" gap={1}>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 4 }}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.visit_request_information_list.business_place_name')}
                        <FormLabel required sx={{ fontSize: '0.75rem' }} />
                      </Typography>
                      <Select
                        value={businessPlaceId === 0 ? '' : String(businessPlaceId)} // businessPlaceName이 있으면 그것을, 아니면 businessPlaceId 값을 표시
                        variant="outlined"
                        size="small"
                        sx={{ flexGrow: 1, width: '150px' }}
                        onChange={handleBusinessPlaceIdChange} // 값 변경 시 호출되는 핸들러
                        disabled={Boolean(id)}
                      >
                        {/* businessPlaces 배열을 기반으로 MenuItem을 동적으로 생성 */}
                        {basicBusinessPlaces?.map((place) => (
                          <MenuItem key={place.id} value={place.id} sx={{ fontSize: '12px' }}>
                            {place.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {/* 담당자 입력 필드 */}
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography
                          minWidth={100}
                          variant="caption"
                          color={theme.palette.fieldText.title}
                          height="32px"
                          lineHeight="normal"
                          display="flex"
                          alignItems="center"
                        >
                          {t('visit_request.visit_request_information_list.manager_name')}
                          <FormLabel required sx={{ fontSize: '0.75rem' }} />
                        </Typography>
                        <TextField
                          value={managerEmail}
                          onChange={handleManagerChange}
                          onBlur={handleManagerBlur} // 포커스를 벗어날 때 유효성 검사 실행
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleManagerBlur();
                            }
                          }} // 엔터 키 입력 시 handleManagerBlur 호출
                          error={managerTouched && !managerId && !!managerErrMsg} // 필드가 떠난 후에만 에러 표시
                          sx={{ flexGrow: 1, width: '150px' }}
                          type="text"
                          size="small"
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ marginLeft: -1, marginRight: -0.5 }}>
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                          disabled={Boolean(id)} // 신청 후 담당자는 변경 불가
                        />
                        <Typography display="flex" alignItems="center" sx={{ fontSize: '13px' }}>
                          @ulvac.com
                        </Typography>
                      </Box>
                      {/* 에러/성공 메시지 */}
                      {managerTouched && (
                        <Typography
                          sx={{
                            color: managerErrMsg ? 'error.main' : 'success.main',
                            fontSize: '13px',
                            marginTop: '4px',
                            marginLeft: 13
                          }}
                        >
                          {managerErrMsg ? managerErrMsg : t('visit_request.visit_request_information_list.manager_name_ok_msg')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.visit_request_information_list.visit_date')}
                        <FormLabel required sx={{ fontSize: '0.75rem' }} />
                      </Typography>
                      <DatePicker
                        format="YYYY-MM-DD"
                        value={dates[0]}
                        onChange={(newValue) => handleDateChange(newValue, 'start')}
                        minDate={dayjs()}
                        slotProps={{
                          calendarHeader: {
                            format: 'YYYY-MM'
                          },
                          textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
                        }}
                        sx={{
                          flexGrow: 1,
                          width: '140px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem' // 아이콘 크기 조정
                          }
                        }}
                        disabled={isEditDisableStatus}
                      />
                      <Box margin="auto 4px">~</Box>
                      <DatePicker
                        format="YYYY-MM-DD"
                        value={dates[1]}
                        onChange={(newValue) => handleDateChange(newValue, 'end')}
                        minDate={dates[0] ?? dayjs()} // 시작 날짜 이후부터 선택 가능
                        maxDate={dates[0] ? dayjs(dates[0]).add(15, 'day') : dayjs().add(15, 'day')} // 시작일로부터 최대 15일까지 선택 가능
                        slotProps={{
                          calendarHeader: {
                            format: 'YYYY-MM'
                          },
                          textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
                        }}
                        sx={{
                          flexGrow: 1,
                          width: '140px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem' // 아이콘 크기 조정
                          }
                        }}
                        disabled={isEditDisableStatus}
                      />
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.visit_request_information_list.visit_time')}
                        <FormLabel sx={{ fontSize: '0.75rem' }} />
                      </Typography>
                      <TimePicker
                        value={times[0]}
                        onChange={(newValue) => handleTimeChange(newValue, 'start')}
                        ampm={false} // 24시간 형식 사용 안함
                        sx={{
                          flexGrow: 1,
                          width: '140px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem' // 아이콘 크기 조정
                          }
                        }}
                        disabled={isEditDisableStatus}
                      />
                      <Box sx={{ margin: 'auto 4px' }}>~</Box>
                      <TimePicker
                        value={times[1]}
                        onChange={(newValue) => handleTimeChange(newValue, 'end')}
                        ampm={false} // 24시간 형식 사용 안함
                        sx={{
                          flexGrow: 1,
                          width: '140px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem' // 아이콘 크기 조정
                          }
                        }}
                        disabled={isEditDisableStatus}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                    {t('visit_request.visit_request_information_list.purpose')}
                    <FormLabel required sx={{ fontSize: '0.75rem' }} />
                  </Typography>

                  <TextField
                    type="text"
                    value={purpose}
                    onChange={handlePurposeChange}
                    size="small"
                    variant="outlined"
                    maxRows={2}
                    multiline
                    sx={{
                      flexGrow: 1,
                      '& .MuiInputBase-root': {
                        paddingX: 0
                      },
                      '& .MuiOutlinedInput-root': {
                        height: 'auto'
                      }
                    }}
                    disabled={isEditDisableStatus}
                  />
                </Box>
              </Box>
            </Box>

            {/* 방문자 정보 */}
            <Box borderRadius={1} bgcolor={theme.palette.contentBlock.background} padding={2} display="flex" flexDirection="column" gap={2}>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
                sx={{
                  flexWrap: { xs: 'wrap', sm: 'nowrap' } // 모바일 뷰에서 아이콘이 줄 바꿈되도록 설정
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  {t('visit_request.visitor_information')}
                </Typography>

                {/* 상태에 따라 방문자 정보 플러스 아이콘 여부, 신청자일 경우만 */}
                {(status == '' || status === 'REQUESTED') && (!id || isRequester) && (
                  <IconButton size="small" color="primary" onClick={handleAddClick} sx={{ padding: '2px' }}>
                    <PersonAddAlt1 sx={{ fontSize: '24px' }} />
                  </IconButton>
                )}
              </Box>

              {visitors?.map((visitor, index) => (
                <Box
                  key={index}
                  borderRadius={1}
                  bgcolor={theme.palette.contentBlock.main}
                  boxShadow={1}
                  padding={2}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="bold">
                        {visitor.name}
                      </Typography>
                      {/* 우선 VIP 색상 진한금색으로 설정, 추후 변경가능성 있음 */}
                      {visitor.isVip && (
                        <Chip
                          label="VIP"
                          size="small"
                          sx={{ marginLeft: '10px', backgroundColor: theme.palette.mode == 'dark' ? '#DBC000' : '#FFEB3B', color: 'black' }}
                        />
                      )}
                    </Box>

                    {/* 클릭한 상태가 없을때와 요청중인 상태일때만 편집, 삭제 버튼 나오도록 , 신청자일 경우만 */}
                    {(status == '' || status === 'REQUESTED') && (!id || isRequester) && (
                      <Box>
                        <IconButton size="small" color="default" onClick={() => handleEditClick(visitor)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(visitor.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }} // 모바일에서 세로 배치, 데스크탑에서 가로 배치
                    gap={{ xs: 1, sm: 4 }}
                  >
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                          {t('visit_request.visitor_information_list.birth')}
                          <FormLabel required sx={{ fontSize: '0.75rem' }} />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {visitor.birth}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                          {t('visit_request.visitor_information_list.phone_number')}
                          <FormLabel required sx={{ fontSize: '0.75rem' }} />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {visitor.phoneNumber}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                          {t('visit_request.visitor_information_list.email')}
                          <FormLabel required sx={{ fontSize: '0.75rem' }} />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {visitor.email}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                          {t('visit_request.visitor_information_list.company')}
                          <FormLabel required sx={{ fontSize: '0.75rem' }} />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {visitor.companyName}
                          {visitor.companyPhoneNumber && ` (${visitor.companyPhoneNumber})`}
                        </Typography>
                      </Box>
                      {/*<Box display="flex" flexDirection="row" alignItems="center" gap={1}>*/}
                      {/*  <Typography width={100} variant="caption" color={theme.palette.fieldText.title}>*/}
                      {/*    {t('visit_request.visitor_information_list.company_phone_number')}*/}
                      {/*    <FormLabel required sx={{ fontSize: '0.75rem' }} />*/}
                      {/*  </Typography>*/}
                      {/*  <Typography variant="body2" fontWeight="bold">*/}
                      {/*    {visitor.companyPhoneNumber}*/}
                      {/*  </Typography>*/}
                      {/*</Box>*/}
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                          {t('visit_request.visitor_information_list.blood_type')}
                          <FormLabel required sx={{ fontSize: '0.75rem' }} />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {visitor.bloodType}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                          {t('visit_request.visitor_information_list.car_number')}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {visitor.carNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request.visitor_information_list.note')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {visitor.note}
                    </Typography>
                  </Box>

                  <Accordion
                    sx={{
                      // backgroundColor: '#F5F5F5',
                      backgroundColor: theme.palette.contentBlock.background,
                      borderRadius: '8px',
                      marginTop: '0 !important',
                      display: 'flex', // 플렉스 박스 사용
                      flexDirection: 'column',
                      boxShadow: 'none',
                      '&::before': {
                        display: 'none'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                      sx={{
                        minHeight: '48px',
                        height: '48px',
                        '&.Mui-expanded': {
                          minHeight: '48px',
                          height: '48px'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '0 !important'
                        }
                      }}
                    >
                      <Typography minWidth={100} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request.visitor_information_list.digital_devices.digital_devices')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {visitor.digitalDevices ? visitor.digitalDevices.length : '0'}
                        {t('visit_request.visitor_information_list.digital_devices.mount')}
                      </Typography>
                    </AccordionSummary>
                    <Divider variant="fullWidth" sx={{ mx: 2 }} />
                    <AccordionDetails sx={{ padding: 2 }}>
                      <Box>
                        {visitor.digitalDevices && visitor.digitalDevices.length > 0 ? (
                          visitor.digitalDevices.map((device, index) => (
                            <Box key={index} display="flex" flexDirection="row" alignItems="center" gap={1}>
                              <Icon sx={{ fontSize: '16px' }}>•</Icon>
                              <Typography variant="body2" fontWeight="bold">
                                {basicDigitalDevices?.find((d) => d.id === device.digitalDeviceId)
                                  ? (basicDigitalDevices?.find((d) => d.id === device.digitalDeviceId)?.name || '') +
                                    (device.modelName && `(${device.modelName})`)
                                  : t('visit_request.visitor_information_list.digital_devices.choose_devices')}
                              </Typography>
                              <Typography variant="caption" color={theme.palette.mode == 'dark' ? '#bfbfbf' : '#7f7f7f'}>
                                {device.serialNumber}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2">{t('visit_request.visitor_information_list.digital_devices.no_devices')}</Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              ))}
            </Box>

            {(!id || isRequester || isManager) && (
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                {/* 내역에서 클릭한 데이터의 상태가 있을때는 다른 버튼 보여줌 */}
                {status ? (
                  <VisitRequestBtn
                    status={status != 'APPROVED' || visitors?.some((visitor) => !visitor.securityManageNumber) ? status : 'REQUEST_COMPLETED'}
                    isManager={isManager}
                    onChange={handleChange}
                    onCancel={handleCancel}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleSubmit} // 버튼 클릭 시 handleSubmit 함수 호출
                    sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
                  >
                    {t('visit_request.visitor_information_list.button_name.request')}
                  </Button>
                )}

                {/* 승인, 반려, 취소 상태 시 재신청 가능 */}
                {Boolean(id) && isRequester && status && ['REJECTED', 'CANCELED', 'APPROVED'].includes(status) && (
                  <Button
                    variant="contained"
                    color="secondary"
                    size="medium"
                    onClick={handleResubmit} // 버튼 클릭 시 handleResubmit 함수 호출
                    sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
                  >
                    {t('visit_request.visitor_information_list.button_name.re_request')}
                  </Button>
                )}
              </Box>
            )}

            {/* 팝업 컴포넌트 */}
            {openPopup && (
              <VisitPopup
                digitalDevices={basicDigitalDevices}
                visitor={selectedVisitor}
                addVisitor={addVisitor}
                updateVisitor={updateVisitor}
                newId={getNewVisitorId}
                onClose={handleClosePopup}
                open={openPopup}
              />
            )}
          </Box>
        </LocalizationProvider>
      ) : (
        isAgreed && (
          <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
            <CircularProgress color="primary" />
          </Box>
        )
      )}
    </Box>
  );
}
